import * as v from '@badrap/valita'
import { api } from 'src/api'
import { makeDateTime } from 'src/utils'
import { validOrgSchema } from '../Client/Info/api'

const logLevelSchema = v.union(
  v.literal('info'),
  v.literal('debug'),
  v.literal('error'),
  v.null(),
)

export type LogLevel = Exclude<v.Infer<typeof logLevelSchema>, null>

export const getLogLevel = () =>
  api({
    schema: v
      .object({
        result: v.literal('OK'),
        data: v.record(logLevelSchema),
      })
      .chain((data) => v.ok(data.data)),
    method: 'GET',
    url: '/getloglevel',
    withAuth: false,
  })

export const changeLogLevel = (parms: { server: string; loglevel: LogLevel }) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/changeloglevel',
    body: parms,
    withAuth: true,
  })

const validOrganizationsSchema = v.array(
  v.object({
    organization: v.string(),
    code: v.string().optional(),
  }),
)
export const getAdminOrganizations = (params: { username: string }) =>
  api({
    schema: v
      .object({
        result: v.literal('OK'),
        organizations: validOrganizationsSchema,
      })
      .chain((data) => v.ok(data.organizations)),
    method: 'POST',
    body: params,
    url: '/getadminorganizations',
    withAuth: true,
  })

export type Organization = {
  billing?:
    | 'free'
    | 'billable'
    | 'trial'
    | 'custom'
    | 'contract'
    | 'contract_trial'
  organization: string
  application?: Record<string, string | number | Record<string, string>>
  aws_connect_saml_url?: string
  aws_connect_type?: 'CCP' | 'SAML' | 'SSO' | 'SALESFORCE'
  code?: string
  cognito_setup_url?: string
  cognito_url?: string
  failedAgentConnectTimer?: string
  iam_app_logging_accessKey?: string
  iam_app_logging_accessSecret?: string
  iam_app_logging_region?: string
  iam_organization_admin_accessKey?: string
  iam_organization_admin_accessSecret?: string
  iam_organization_admin_region?: string
  iam_organization_policy_arn?: string
  iam_organization_usergroup?: string
  iam_organizaton_api_version?: string
  organization_APNS_topic?: string
  organization_connect_arn?: string
  redirect_uri?: string
  setupStep?: 'setup' | 'salesforce-configuration' | 'finished'
  setupuserPoolAppClientId?: string
  setupuserPoolClientSecret?: string
  setupuserPoolId?: string
  setupuserPoolRegion?: string
  userPoolAppClientId?: string
  userPoolClientSecret?: string
  userPoolId?: string
  userPoolRegion?: string
  userPoolTokenType?: string
  voippushserver_apnsKeyId?: string
  voippushserver_apnTeamId?: string
  voippushserver_production?: string
  voipserver_apnsp8_binary?: string
  createtimestamp?: number
  edittimestamp?: number
}

export const getAdminOrganization = (params: {
  organization: string
  username: string
}) =>
  api({
    schema: v
      .object({
        result: v.literal('OK'),
        organization: validOrgSchema,
      })
      .chain((data) => v.ok(data.organization)),
    method: 'POST',
    body: params,
    url: '/getadminorganization',
    withAuth: true,
  })

export const editAdminOrganization = (
  params: Organization & { username: string },
) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    body: params,
    url: '/editadminorganization',
    withAuth: true,
  })

export const encrypt = (params: { text: string }) =>
  api({
    schema: v
      .object({
        data: v.string(),
        result: v.literal('OK'),
      })
      .chain((data) => v.ok(data.data)),
    method: 'POST',
    body: params,
    url: '/encrypt',
    withAuth: true,
  })

export const decrypt = (params: { text: string }) =>
  api({
    schema: v
      .object({
        data: v.string(),
        result: v.literal('OK'),
      })
      .chain((data) => v.ok(data.data)),
    method: 'POST',
    body: params,
    url: '/decrypt',
    withAuth: true,
  })

export const getServerVersion = () =>
  api({
    schema: v
      .object({
        data: v.string(),
        result: v.literal('OK'),
      })
      .chain((data) => v.ok(data.data)),
    method: 'GET',
    url: '/getserverversion',
    withAuth: true,
  })

export const getNotMappedRegions = (params: {
  regionstoscan: { mc_region: string; url: string }[]
  username: string
}) =>
  api({
    schema: v.object({
      missingregions: v.array(
        v.object({
          region: v.string(),
          organization: v.string(),
          customerid: v.string(),
        }),
      ),
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/getnotmappedregions',
    withAuth: true,
    body: params,
    region: 'eu-central-1',
  })

export const getNotMappedSubscribers = (params: {
  regionstoscan: { mc_region: string; url: string }[]
  username: string
}) =>
  api({
    schema: v.object({
      allmissingorganizations: v.array(
        v.object({
          contactEmail: v.string().optional(),
          successfully_subscribed: v.boolean().optional(),
          companyName: v.string().optional(),
          isfreetrialtermpresent: v.boolean().optional(),
          subscription_expired: v.boolean().optional(),
          contactPhone: v
            .string()
            .optional()
            .chain((data) => v.ok(data?.replaceAll(' ', ''))),
          customerIdentifier: v.string(),
        }),
      ),
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/getnotmappedsubscribers',
    withAuth: true,
    body: params,
    region: 'eu-central-1',
  })

export const getOrganizationsLastUsed = (params: {
  regionstoscan: { mc_region: string; url: string }[]
  username: string
}) =>
  api({
    schema: v.object({
      organizationslastused: v
        .array(
          v.object({
            organization: v.string(),
            code: v.string(),
            lastused: v
              .string()
              .chain((data: string) =>
                v.ok(data === 'never used' ? 'Never used' : makeDateTime(data)),
              ),
            enabled: v.string(),
            subscriptionexpired: v.string(),
            mc_region: v.string(),
            customerIdentifier: v.string(),
            setupuserPoolId: v.string(),
            setupStep: v.union(v.literal('finished'), v.literal('setup')),
            createtimestamp: v
              .number()
              .optional()
              .chain((data) => (data ? v.ok(makeDateTime(data)) : v.ok(''))),
          }),
        )
        .default([]),
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/getorganizationslastused',
    withAuth: true,
    body: params,
    region: 'eu-central-1',
  })

export const deleteFullOrganization = (params: {
  regionstoscan: { mc_region: string; url: string }[]
  organizationtodelete: string
  username: string
}) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
      progress_stack: v.array(v.string()),
    }),
    method: 'POST',
    url: '/deletefullorganization',
    withAuth: true,
    body: params,
    region: 'eu-central-1',
  })

export const deleteOrgRegion = (params: {
  organizationtodelete: string
  username: string
}) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/deleteorgregions',
    withAuth: true,
    body: params,
    region: 'eu-central-1',
  })

export const deleteAWSSubscriber = (params: {
  customerid: string
  username: string
}) =>
  api({
    schema: v.object({
      result: v.literal('OK'),
    }),
    method: 'POST',
    url: '/deleteawssubscriber',
    withAuth: true,
    body: params,
    region: 'eu-central-1',
  })
